// ProductImageUpload component for merchant product creation
// Enhanced multi-image upload with main image selection and drag-drop reordering
// Based on product-merchant-crud.html design requirements

import React, { useState, useCallback, useRef, useEffect } from 'react'

// Utility function for class names  
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ')
}

export interface ProductImageItem {
  id: string
  url: string
  isMain: boolean
  alt: string
  file?: File
  name?: string
  order: number
  size?: number
  uploaded?: boolean
  // File upload integration fields
  file_id?: string  // Server file ID from upload
  file_hash?: string // File hash for URL-based images (required when using file_url mode)
  upload_status?: 'pending' | 'uploading' | 'success' | 'error'
  upload_progress?: number
  error_message?: string
}

// Type alias for backwards compatibility
export type ProductImageData = ProductImageItem

export interface ProductImageUploadProps {
  /** Array of product images */
  images: ProductImageItem[]
  /** Change handler when images are modified */
  onChange: (images: ProductImageItem[]) => void
  /** Maximum number of images allowed */
  maxImages?: number
  /** Maximum file size in MB */
  maxFileSize?: number
  /** Whether the component is disabled */
  disabled?: boolean
  /** Validation error */
  error?: string
  /** Additional CSS classes */
  className?: string
  /** Component ID */
  id?: string
  /** Test ID */
  'data-testid'?: string
  /** Upload handler for async upload */
  onUpload?: (files: File[]) => Promise<string[]>
  /** Show image count and limits */
  showImageCount?: boolean
  /** File upload handler for server-side file management */
  onFileUpload?: (file: File) => Promise<{ file_id: string; file_url: string }>
}

const ProductImageUpload: React.FC<ProductImageUploadProps> = ({
  images = [],
  onChange,
  maxImages = 10,
  maxFileSize = 5,
  disabled = false,
  error,
  className = '',
  id: providedId,
  'data-testid': dataTestId,
  onUpload,
  onFileUpload,
  showImageCount = true,
  ...props
}) => {
  const generatedId = `product-image-upload-${Math.random().toString(36).substr(2, 9)}`
  const id = providedId || generatedId
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  // Ensure images is always an array
  const safeImages = images || []

  // Check if main image is selected
  const hasMainImage = safeImages.some(img => img.isMain)
  const canAddMore = safeImages.length < maxImages

  // File validation
  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File ${file.name} is too large. Maximum size is ${maxFileSize}MB`
    }
    
    if (!file.type.startsWith('image/')) {
      return `File ${file.name} is not a valid image`
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      return `File ${file.name} format not supported. Use JPG, PNG, GIF, or WEBP`
    }

    return null
  }

  // Handle file selection
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const remainingSlots = maxImages - safeImages.length
    const filesToProcess = fileArray.slice(0, remainingSlots)

    // Validate files
    const validationErrors: string[] = []
    filesToProcess.forEach(file => {
      const error = validateFile(file)
      if (error) validationErrors.push(error)
    })

    if (validationErrors.length > 0) {
      console.error('File validation errors:', validationErrors)
      return
    }

    setIsUploading(true)

    try {
      let newImages: ProductImageItem[] = []

      if (onFileUpload) {
        // New server-side file upload mode
        // Create images with blob URLs for immediate preview and pending status
        newImages = filesToProcess.map((file, index) => ({
          id: `img_${Date.now()}_${index}`,
          url: URL.createObjectURL(file), // Blob URL for immediate preview
          isMain: false,
          alt: file.name,
          file,
          name: file.name,
          order: safeImages.length + index,
          upload_status: 'pending' as const,
          upload_progress: 0
        }))

        // Add images to state immediately for preview
        const allImages = [...safeImages, ...newImages]
        onChange(allImages)

        // Upload files to server in parallel (max 3 concurrent)
        const uploadPromises = newImages.map(async (imageItem, index) => {
          const imageId = imageItem.id

          // Update status to uploading
          const updatingImages = [...allImages]
          const imgIndex = updatingImages.findIndex(img => img.id === imageId)
          if (imgIndex !== -1) {
            updatingImages[imgIndex] = {
              ...updatingImages[imgIndex],
              upload_status: 'uploading' as const
            }
            onChange(updatingImages)
          }

          try {
            // Upload to server
            const result = await onFileUpload(imageItem.file!)

            // Update with server file_id and success status
            const successImages = [...allImages]
            const successIndex = successImages.findIndex(img => img.id === imageId)
            if (successIndex !== -1) {
              successImages[successIndex] = {
                ...successImages[successIndex],
                file_id: result.file_id,
                url: result.file_url, // Replace blob URL with server URL
                upload_status: 'success' as const,
                upload_progress: 100
              }
              onChange(successImages)
            }
          } catch (error: any) {
            // Update with error status
            const errorImages = [...allImages]
            const errorIndex = errorImages.findIndex(img => img.id === imageId)
            if (errorIndex !== -1) {
              errorImages[errorIndex] = {
                ...errorImages[errorIndex],
                upload_status: 'error' as const,
                error_message: error?.message || 'Upload failed'
              }
              onChange(errorImages)
            }
          }
        })

        // Wait for all uploads to complete
        await Promise.all(uploadPromises)

      } else if (onUpload) {
        // Legacy async upload mode
        const uploadedUrls = await onUpload(filesToProcess)
        newImages = filesToProcess.map((file, index) => ({
          id: `img_${Date.now()}_${index}`,
          url: uploadedUrls[index] || URL.createObjectURL(file),
          isMain: false,
          alt: file.name,
          file,
          name: file.name,
          order: safeImages.length + index
        }))
        onChange([...safeImages, ...newImages])
      } else {
        // Local preview mode
        newImages = filesToProcess.map((file, index) => ({
          id: `img_${Date.now()}_${index}`,
          url: URL.createObjectURL(file),
          isMain: false,
          alt: file.name,
          file,
          name: file.name,
          order: safeImages.length + index
        }))
        onChange([...safeImages, ...newImages])
      }
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setIsUploading(false)
    }
  }, [images, maxImages, onUpload, onFileUpload, onChange, maxFileSize])

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files)
    e.target.value = '' // Reset input
  }

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragOver(true)
    }
  }, [disabled])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    
    if (!disabled) {
      handleFileSelect(e.dataTransfer.files)
    }
  }, [disabled, handleFileSelect])

  // Remove image
  const removeImage = useCallback((imageId: string) => {
    const updatedImages = safeImages.filter(img => img.id !== imageId)
    
    // No longer automatically set first image as main when main image is removed
    // User must manually select a new main image - matches HTML design behavior
    
    // Reorder remaining images
    const reorderedImages = updatedImages.map((img, index) => ({
      ...img,
      order: index
    }))
    
    onChange(reorderedImages)
  }, [safeImages, onChange])

  // Set main image
  const setAsMainImage = useCallback((imageId: string) => {
    const updatedImages = safeImages.map(img => ({
      ...img,
      isMain: img.id === imageId
    }))
    onChange(updatedImages)
  }, [safeImages, onChange])

  // Retry failed upload
  const retryUpload = useCallback(async (imageId: string) => {
    if (!onFileUpload) return

    const image = safeImages.find(img => img.id === imageId)
    if (!image || !image.file) return

    // Update status to uploading
    const updatingImages = safeImages.map(img =>
      img.id === imageId
        ? { ...img, upload_status: 'uploading' as const, error_message: undefined }
        : img
    )
    onChange(updatingImages)

    try {
      // Upload to server
      const result = await onFileUpload(image.file)

      // Update with server file_id and success status
      const successImages = safeImages.map(img =>
        img.id === imageId
          ? {
              ...img,
              file_id: result.file_id,
              url: result.file_url,
              upload_status: 'success' as const,
              upload_progress: 100,
              error_message: undefined
            }
          : img
      )
      onChange(successImages)
    } catch (error: any) {
      // Update with error status
      const errorImages = safeImages.map(img =>
        img.id === imageId
          ? {
              ...img,
              upload_status: 'error' as const,
              error_message: error?.message || 'Upload failed'
            }
          : img
      )
      onChange(errorImages)
    }
  }, [safeImages, onFileUpload, onChange])

  // Trigger file input
  const triggerFileInput = () => {
    if (fileInputRef.current && !disabled && canAddMore) {
      fileInputRef.current.click()
    }
  }

  const containerClasses = classNames(
    'tw-w-full tw-flex tw-flex-col tw-gap-3',
    className
  )

  const addImageTileClasses = classNames(
    'tw-aspect-square tw-rounded-lg tw-border-2 tw-border-dashed tw-flex tw-flex-col tw-items-center tw-justify-center tw-transition-all tw-group tw-relative tw-overflow-hidden',
    isDragOver
      ? 'tw-border-primary tw-bg-primary/5 dark:tw-bg-primary/10'
      : 'tw-border-gray-300 dark:tw-border-gray-600',
    disabled
      ? 'tw-bg-gray-50 dark:tw-bg-gray-800 tw-cursor-not-allowed'
      : 'tw-cursor-pointer hover:tw-border-primary hover:tw-bg-primary/5 dark:hover:tw-bg-primary/10',
    canAddMore ? '' : 'tw-opacity-50'
  )

  return (
    <div className={containerClasses} id={id} data-testid={dataTestId} {...props}>
      {/* Unified Grid: Add Image tile + Uploaded Images */}
      <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-5 tw-gap-4">
        {/* Add Image Tile - Always first in grid */}
        {canAddMore && (
          <div
            className={addImageTileClasses}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={triggerFileInput}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleInputChange}
              disabled={disabled}
              className="tw-hidden"
              aria-label="Upload product images"
            />
            
            <span className="material-symbols-outlined tw-text-3xl tw-text-gray-400 group-hover:tw-text-primary tw-transition-colors">
              {isUploading ? 'upload' : 'add_photo_alternate'}
            </span>
            <span className="tw-text-xs tw-font-medium tw-text-gray-500 group-hover:tw-text-primary tw-mt-2 tw-transition-colors">
              {isUploading ? 'Uploading...' : 'Add Image'}
            </span>
          </div>
        )}

        {/* Uploaded Images */}
        {safeImages.map((image) => (
          <div key={image.id} className="tw-relative">
            {/* Image Container - Match HTML design with ring border for main image */}
            <div className={classNames(
              'tw-aspect-square tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-overflow-hidden tw-bg-gray-50 dark:tw-bg-[#101922] tw-relative',
              image.isMain
                ? 'tw-ring-2 tw-ring-primary tw-ring-offset-2 tw-ring-offset-white dark:tw-ring-offset-[#1C252E]'
                : ''
            )}>
              <img
                src={image.url}
                alt={image.alt}
                className={classNames(
                  'tw-w-full tw-h-full tw-object-cover',
                  image.upload_status === 'uploading' ? 'tw-opacity-50' : ''
                )}
              />

              {/* Upload Status Overlay */}
              {image.upload_status === 'uploading' && (
                <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/30">
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-4 tw-border-white tw-border-t-transparent" />
                    <span className="tw-text-white tw-text-xs tw-font-medium">Uploading...</span>
                  </div>
                </div>
              )}

              {/* Success Badge */}
              {image.upload_status === 'success' && (
                <div className="tw-absolute tw-bottom-2 tw-right-2 tw-bg-green-500 tw-text-white tw-p-1 tw-rounded-full tw-shadow-lg">
                  <span className="material-symbols-outlined tw-text-[14px]">check_circle</span>
                </div>
              )}

              {/* Error Badge */}
              {image.upload_status === 'error' && (
                <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-red-500/20">
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-4">
                    <span className="material-symbols-outlined tw-text-red-500 tw-text-3xl">error</span>
                    <span className="tw-text-red-600 dark:tw-text-red-400 tw-text-xs tw-font-medium tw-text-center">
                      {image.error_message || 'Upload failed'}
                    </span>
                    <button
                      onClick={() => retryUpload(image.id)}
                      className="tw-px-3 tw-py-1 tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors"
                      type="button"
                    >
                      Retry Upload
                    </button>
                  </div>
                </div>
              )}

              {/* Main Image Badge - Match HTML design exactly */}
              {image.isMain && (
                <div className="tw-absolute tw-top-2 tw-left-2 tw-bg-primary tw-text-white tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium tw-flex tw-items-center tw-gap-1">
                  <span className="material-symbols-outlined tw-text-[14px]">star</span>
                  Main Image
                </div>
              )}

              {/* Remove button (always visible) - Match HTML design */}
              <button
                onClick={() => removeImage(image.id)}
                className="tw-absolute tw-top-2 tw-right-2 tw-p-1.5 tw-bg-red-500/90 hover:tw-bg-red-600 tw-rounded-md tw-text-white tw-backdrop-blur-sm tw-transition-colors tw-shadow-lg"
                title="Remove image"
                type="button"
              >
                <span className="material-symbols-outlined tw-text-[16px]">close</span>
              </button>
            </div>

            {/* Always visible action buttons below image - Match HTML design */}
            <div className="tw-mt-2 tw-flex tw-gap-2">
              <button
                onClick={() => setAsMainImage(image.id)}
                disabled={image.isMain || image.upload_status !== 'success'}
                className={classNames(
                  'tw-flex-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1',
                  image.isMain
                    ? 'tw-bg-primary tw-text-white'
                    : image.upload_status === 'success'
                    ? 'tw-bg-gray-100 dark:tw-bg-gray-800 tw-text-gray-700 dark:tw-text-gray-300 hover:tw-bg-gray-200 dark:hover:tw-bg-gray-700'
                    : 'tw-bg-gray-100 dark:tw-bg-gray-800 tw-text-gray-400 dark:tw-text-gray-600 tw-cursor-not-allowed'
                )}
                type="button"
                title={image.upload_status !== 'success' ? 'Please wait for upload to complete' : ''}
              >
                <span className="material-symbols-outlined tw-text-[14px]">star</span>
                <span>{image.isMain ? 'Main Image' : 'Set as Main'}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Help text and image count info */}
      <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
        Upload up to {maxImages} images (max {maxFileSize}MB each). You must select a
        main image before proceeding.
      </p>

      {/* No main image warning - Match HTML design exactly */}
      {safeImages.length > 0 && !hasMainImage && (
        <div className="tw-mt-2 tw-p-3 tw-bg-amber-50 dark:tw-bg-amber-900/20 tw-border tw-border-amber-200 dark:tw-border-amber-800 tw-rounded-lg">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-amber-700 dark:tw-text-amber-300">
            <span className="material-symbols-outlined tw-text-[16px]">warning</span>
            <span className="tw-text-xs tw-font-medium">
              Please select a main image by clicking "Set as Main" on
              one of your uploaded images.
            </span>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="tw-p-3 tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-rounded-lg">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-red-600 dark:tw-text-red-400">
            <span className="material-symbols-outlined tw-text-[16px]">error</span>
            <span className="tw-text-xs tw-font-medium">{error}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductImageUpload